import React, { useCallback, useContext, useEffect, useMemo } from "react";
import debounce from "lodash/debounce";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { PROCESS, TICKET_STATE } from "src/stores/TicketStore";
import { FilterStateType, SORT, SORT_FIELD } from "src/stores/tickets/TicketListStore";
import { Route, selectRoute } from "src/config/routes";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { TicketsList } from "./list/TicketsList";
import { TagSearch } from "src/components/tagSearch/TagSearch";
import styles from "./Tickets.module.css";

export const getIcon = (type: PROCESS) => {
    switch (type) {
        case PROCESS.DAMAGE_NOTIFICATION:
            return "/icons/damage.svg";
        case PROCESS.COMMON_REQUEST:
            return "/icons/unknown.svg";
        case PROCESS.NAMESIGN:
            return "/icons/doorbell.svg";
        case PROCESS.DOCUMENT_QUESTION:
            return "/icons/document_question.svg";
        case PROCESS.KEY_ORDER:
            return "/icons/keys.svg";
        default:
            return "/icons/unknown.svg";
    }
};

const TicketsBase = () => {
    const { t } = useTranslation();
    const { uiStore, navStore, ticketListStore, ticketStore, authStore } = useContext(RootStoreContext);
    const { setFilter, ticketFilter, clearFilter } = ticketListStore;

    useEffect(() => {
        navStore.setTitle("");
        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);
        navStore.setBackbutton(null);
    }, [navStore]);

    useEffect(() => {
        // Initialize server parameters based on default search tag states
        // Set workflow types if any are active by default
        const activeWorkflowTypes = ticketListStore.activeSearchTags
            .filter((tag) => tag.type && ["1", "2", "3", "4", "5"].includes(tag.id))
            .map((tag) => parseInt(tag.id));

        if (activeWorkflowTypes.length > 0) {
            ticketListStore.setWorkflowTypes(`{${activeWorkflowTypes.join(",")}}`);
        }

        // Set user ID if "Show Own" is active by default
        const showOwn = ticketListStore.activeSearchTags.some((tag) => tag.id === "SHOW_OWN");
        if (showOwn && authStore.user?.userid) {
            // When "Show Own" is active, filter by current user's ID
            // Format as PostgreSQL array literal
            ticketListStore.setAssignedUserIds(`{${authStore.user.userid}}`);
        }

        ticketListStore.loadTickets();

        // Load all tickets once component is rendered
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        ticketStore.startLoadAllTicketsTimer();
        return () => {
            ticketStore.clearLoadAllTicketsTimer();
        };
    }, [ticketStore]);

    const onFilterChange = useCallback(
        (id: FilterStateType) => {
            setFilter({ ...ticketFilter, state: id });

            // Convert UI state to server state filter and reload tickets
            let serverStateFilter: string | null = null;
            switch (id) {
                case "All":
                    serverStateFilter = "{0, 5, 10, 20, 90, 99}"; // All tickets
                    break;
                case "AllOpen":
                    serverStateFilter = "{0, 10, 20}";
                    break;
                case TICKET_STATE.NEW:
                    serverStateFilter = "{0, 5}";
                    break;
                case TICKET_STATE.ASSIGNED:
                    serverStateFilter = "{10}";
                    break;
                case TICKET_STATE.COMISSIONED:
                    serverStateFilter = "{20}";
                    break;
                case TICKET_STATE.COMPLETED:
                    serverStateFilter = "{90}";
                    break;
                case TICKET_STATE.ARCHIVED:
                    serverStateFilter = "{99}";
                    break;
            }

            // Reset page offset when changing tabs
            ticketListStore.setPageOffset(0, false);
            ticketListStore.setStateFilter(serverStateFilter || "{0}");
            ticketListStore.loadTickets();
        },
        [ticketFilter, setFilter, ticketListStore]
    );

    const onSortChange = useCallback(
        (newSort: SORT) => {
            setFilter({ ...ticketFilter, sort: newSort });

            // Update server-side sort parameters and reload
            const sortDirection = newSort === SORT.DATE_NEWEST ? "desc" : "asc";
            ticketListStore.setSortField(SORT_FIELD.DATE, false);
            ticketListStore.setSortDirection(sortDirection);
            ticketListStore.setPageOffset(0, false);
            ticketListStore.loadTickets();
        },
        [ticketFilter, setFilter, ticketListStore]
    );

    const debouncedSearch = useMemo(
        () =>
            debounce((query: string) => {
                ticketListStore.setSearchQuery(query);
                ticketListStore.setPageOffset(0, false);
                ticketListStore.loadTickets();
            }, 500),
        [ticketListStore]
    );

    // Cleanup debounced function on component unmount
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const onChangeSearchQuery = useCallback(
        (query: string) => {
            setFilter({ ...ticketFilter, query });

            debouncedSearch(query);
        },
        [ticketFilter, setFilter, debouncedSearch]
    );

    const sortItems = useMemo(
        () => [
            { label: t("screens.tickets.sort.dateNewest"), value: SORT.DATE_NEWEST },
            { label: t("screens.tickets.sort.dateOldest"), value: SORT.DATE_OLDEST }
        ],
        [t]
    );

    const onUpdateSearchTag = useCallback(
        (tagId: string, status: boolean) => {
            ticketListStore.setTag(tagId, status);

            // Get all active tags after this update
            const updatedTags = ticketListStore.searchTags.map((tag) =>
                tag.id === tagId ? { ...tag, active: status } : tag
            );

            // Map active tags to server parameters
            const activeWorkflowTypes = updatedTags
                .filter((tag) => tag.active && tag.type && ["1", "2", "3", "4", "5"].includes(tag.id))
                .map((tag) => parseInt(tag.id));

            // Update server parameters
            if (activeWorkflowTypes.length > 0) {
                // Convert to bracketed format like {2,3}
                ticketListStore.setWorkflowTypes(`{${activeWorkflowTypes.join(",")}}`);
            } else {
                ticketListStore.setWorkflowTypes(null);
            }

            const showOwn = updatedTags.find((tag) => tag.id === "SHOW_OWN")?.active || false;
            if (showOwn && authStore.user?.userid) {
                // When "Show Own" is active, filter by current user's ID
                // Format as PostgreSQL array literal
                ticketListStore.setAssignedUserIds(`{${authStore.user.userid}}`);
            } else {
                ticketListStore.setAssignedUserIds(null);
            }

            const showMyRealestate = updatedTags.find((tag) => tag.id === "SHOW_MYREALESTATE")?.active || false;
            if (showMyRealestate && authStore.user?.userid) {
                ticketListStore.setFilterRealestatesByUserIds(`{${authStore.user.userid}}`);
            } else {
                ticketListStore.setFilterRealestatesByUserIds(null);
            }

            // SHOW_REALESTATE and SHOW_UNIT handling - extract ID from tag id (format: SHOW_REALESTATE:uuid)
            const realestateTag = updatedTags.find((tag) => tag.id.startsWith("SHOW_REALESTATE:"));
            if (realestateTag) {
                const realestateId = realestateTag.id.split(":")[1];
                if (realestateTag.active) {
                    ticketListStore.setRealestateId(realestateId);
                } else {
                    ticketListStore.setRealestateId("");
                }
            }

            const unitTag = updatedTags.find((tag) => tag.id.startsWith("SHOW_UNIT:"));
            if (unitTag) {
                const unitId = unitTag.id.split(":")[1];
                if (unitTag.active) {
                    ticketListStore.setUnitId(unitId);
                } else {
                    ticketListStore.setUnitId("");
                }
            }

            ticketListStore.setPageOffset(0, false);
            ticketListStore.loadTickets();
        },
        [ticketListStore, authStore.user?.userid]
    );

    const onResetSearch = useCallback(() => {
        clearFilter();
        ticketListStore.loadTickets();
    }, [clearFilter, ticketListStore]);

    return (
        <UI.Container
            className={`pt-5 ${styles.TicketsContainer} ${
                uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP ? styles.MinPadding : ""
            }`}
            fluid
        >
            <UI.Row className="mb-2 ticket-list-header">
                <UI.Col xs={12} sm={{ span: 8 }}>
                    <UI.Title>{t("screens.tickets.sidebar_title")}</UI.Title>
                </UI.Col>
                <UI.Col xs={12} sm={4} md={4} lg={{ span: 4 }}>
                    <UI.Button
                        to={selectRoute(Route.newTicket, authStore.user?.role)}
                        label={t("screens.tickets.list.new_ticket_button")}
                    />
                </UI.Col>
            </UI.Row>
            <UI.Row className="ticket-list-searchbar-row">
                <UI.Col xs={12}>
                    <div className={`${styles.Toolbar} mb-3`}>
                        <div className={styles.ToolbarTagSearch}>
                            <TagSearch
                                searchTags={ticketListStore.searchTags}
                                value={ticketFilter.query}
                                updateTagStatusById={onUpdateSearchTag}
                                onChangeInput={onChangeSearchQuery}
                                reset={onResetSearch}
                            />
                        </div>
                        <div className={styles.ToolbarSort}>
                            <UI.Dropdown
                                id="sort"
                                onSelect={onSortChange}
                                items={sortItems}
                                label={uiStore.isMobile ? "" : t(`screens.tickets.sort.${ticketFilter.sort}`)}
                                icon={uiStore.isMobile && <UI.Icon icon={UI.SVGIcon.Sort} />}
                            />
                        </div>
                    </div>
                </UI.Col>
            </UI.Row>
            <UI.Row className="ticket-list-row">
                <UI.Col xs={12}>
                    <TicketsList onFilterChange={onFilterChange} />
                </UI.Col>
            </UI.Row>
        </UI.Container>
    );
};

export const Tickets = Sentry.withProfiler(observer(TicketsBase));
